/** GPU levels — fragment-shader port of `lib/adjustments/levels.ts`.
 *
 *  Same LUT-pair pattern as Curves: the CPU path applies a combined
 *  RGB LUT then a per-channel LUT. We pre-compose into one LUT per
 *  channel CPU-side and pass each as a uniform float array; the
 *  shader does a single lookup per channel. Byte parity is enforced
 *  by sourcing the LUT values from `buildLevelsLUT` directly — the
 *  same function the CPU dispatch uses.
 *
 *  ── Future portability note ───────────────────────────────────
 *  See the matching note in `curves.ts` — same uniform-array-vs-
 *  texture-LUT consideration applies here. Convert when convolutions
 *  in the next PR force a runtime extension for additional
 *  textures. */

import { buildLevelsLUT, type LevelsValue } from '../../adjustments/levels';
import { applyShader } from '../runtime';

const LEVELS_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform float u_lutR[256];
uniform float u_lutG[256];
uniform float u_lutB[256];
out vec4 outColor;

int idx(float v01) {
  return clamp(int(floor(v01 * 255.0 + 0.5)), 0, 255);
}

void main() {
  vec4 c = texture(u_source, v_uv);
  outColor = vec4(
    u_lutR[idx(c.r)] / 255.0,
    u_lutG[idx(c.g)] / 255.0,
    u_lutB[idx(c.b)] / 255.0,
    c.a
  );
}
`;

function composeLuts(combined: Uint8ClampedArray, perChannel: Uint8ClampedArray): number[] {
  const composed = new Array<number>(256);
  for (let i = 0; i < 256; i++) {
    composed[i] = perChannel[combined[i]];
  }
  return composed;
}

export function applyLevelsOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: LevelsValue,
): boolean {
  const lutRGB = buildLevelsLUT(value.rgb);
  const lutR = composeLuts(lutRGB, buildLevelsLUT(value.r));
  const lutG = composeLuts(lutRGB, buildLevelsLUT(value.g));
  const lutB = composeLuts(lutRGB, buildLevelsLUT(value.b));
  return applyShader({
    source,
    target,
    fragShaderSrc: LEVELS_FRAG,
    uniforms: {
      u_lutR: { type: 'float[]', value: lutR },
      u_lutG: { type: 'float[]', value: lutG },
      u_lutB: { type: 'float[]', value: lutB },
    },
  });
}
